import { useEffect, useState } from "react";
import { Button, BUTTON_SIZE, BUTTON_VARIANT } from "components/UI/Button";
import { CustomSelect, OptionType } from "components/UI/CustomSelect";
import { Input } from "components/UI/Input";
import { Modal } from "components/UI/Modal";
import { ROLL_PLACEMENT, WEIGHT_UNIT_OPTIONS } from "constants/units.contants";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useTranslation } from "localization/localization";
import { selectOptions, setAutoContainersAction, setOptions, setTransport } from "store";
import { convertTransportToUnits } from "utils/units.utils";
import s from "./UnitsSettingsModal.module.scss";

type UnitsSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

// const lengthOptions: OptionType[] = LENGTH_UNIT_OPTIONS.map((option) => ({ ...option }));
const weightOptions: OptionType[] = WEIGHT_UNIT_OPTIONS.map((option) => ({ ...option }));

const SquarePlacement = () => (
  <svg width="120" height="72" viewBox="0 0 120 72" aria-hidden="true">
    {[0, 1, 2].map((row) =>
      [0, 1, 2].map((col) => (
        <rect
          key={`${row}-${col}`}
          x={18 + col * 30}
          y={6 + row * 22}
          width="24"
          height="16"
          rx="2"
          fill="#F2F9FF"
          stroke="#9EA9B9"
        />
      ))
    )}
  </svg>
);

const HexagonPlacement = () => (
  <svg width="120" height="72" viewBox="0 0 120 72" aria-hidden="true">
    {[
      [28, 18],
      [52, 18],
      [76, 18],
      [40, 38],
      [64, 38],
      [92, 38],
      [28, 58],
      [52, 58],
      [76, 58],
    ].map(([cx, cy]) => (
      <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="9" fill="#F2F9FF" stroke="#9EA9B9" />
    ))}
  </svg>
);

export const UnitsSettingsModal = ({ isOpen, onClose }: UnitsSettingsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const options = useAppSelector(selectOptions);
  const containers = useAppSelector((state) => state.root.containers);
  const autoContainers = useAppSelector((state) => state.root.autoContainers);
  const [lengthUnits, setLengthUnits] = useState(options.lengthUnits);
  const [weightUnits, setWeightUnits] = useState(options.weightUnits);
  const [lengthAccuracy, setLengthAccuracy] = useState(String(options.lengthAccuracy ?? 5));
  const [rollPlacement, setRollPlacement] = useState(options.rollPlacement ?? ROLL_PLACEMENT.SQUARE);

  useEffect(() => {
    if (!isOpen) return;
    setLengthUnits(options.lengthUnits);
    setWeightUnits(options.weightUnits);
    setLengthAccuracy(String(options.lengthAccuracy ?? 5));
    setRollPlacement(options.rollPlacement ?? ROLL_PLACEMENT.SQUARE);
  }, [isOpen, options]);

  // const lengthValue = lengthOptions.find((option) => option.value === lengthUnits) ?? lengthOptions[0];
  const weightValue = weightOptions.find((option) => option.value === weightUnits) ?? weightOptions[0];
  const accuracy = Number(lengthAccuracy);
  const canSave = lengthAccuracy.trim() !== "" && !Number.isNaN(accuracy);

  const handleSave = () => {
    if (!canSave) return;

    dispatch(
      setTransport(
        convertTransportToUnits(
          containers as Array<{ spaces?: Array<Record<string, unknown>> }>,
          options.lengthUnits,
          lengthUnits,
          options.weightUnits,
          weightUnits
        ) as typeof containers
      )
    );
    dispatch(
      setAutoContainersAction(
        convertTransportToUnits(autoContainers, options.lengthUnits, lengthUnits, options.weightUnits, weightUnits)
      )
    );
    dispatch(
      setOptions({
        ...options,
        lengthUnits,
        weightUnits,
        lengthAccuracy: accuracy,
        rollPlacement,
      })
    );
    onClose();
  };

  return (
    <Modal isOpen={isOpen} closeButton onClose={onClose} modalClassname={s.modal}>
      <div className={s.content}>
        <p className={s.title}>{t("Units")}</p>
        <div className={s.grid}>
          {/* <div className={s.field}>
            <span className={s.fieldLabel}>{t("Length Units")}</span>
            <CustomSelect<OptionType>
              options={lengthOptions}
              value={lengthValue}
              isSearchable={false}
              handleChange={(option) => setLengthUnits(option.value)}
            />
          </div> */}
          <div className={s.field}>
            <span className={s.fieldLabel}>{t("Mass Units")}</span>
            <CustomSelect<OptionType>
              options={weightOptions}
              value={weightValue}
              isSearchable={false}
              handleChange={(option) => setWeightUnits(option.value)}
            />
          </div>
          <Input
            label={t("Length Accuracy")}
            type="number"
            min={0}
            value={lengthAccuracy}
            onChange={(event) => setLengthAccuracy(event.target.value)}
          />
        </div>

        <div className={s.placement}>
          <p className={s.placementTitle}>{t("Roll Placement")}</p>
          <div className={s.placementOptions}>
            <label className={s.placementItem}>
              <SquarePlacement />
              <span>
                <input
                  type="radio"
                  name="rollPlacement"
                  checked={rollPlacement === ROLL_PLACEMENT.SQUARE}
                  onChange={() => setRollPlacement(ROLL_PLACEMENT.SQUARE)}
                />
                {t("Square")}
              </span>
            </label>
            <label className={s.placementItem}>
              <HexagonPlacement />
              <span>
                <input
                  type="radio"
                  name="rollPlacement"
                  checked={rollPlacement === ROLL_PLACEMENT.HEXAGON}
                  onChange={() => setRollPlacement(ROLL_PLACEMENT.HEXAGON)}
                />
                {t("Hexagon")}
              </span>
            </label>
          </div>
        </div>

        <div className={s.actions}>
          <Button variant={BUTTON_VARIANT.SECONDARY} size={BUTTON_SIZE.L} onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button className={s.save} size={BUTTON_SIZE.L} onClick={handleSave} disabled={!canSave}>
            {t("Save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
